import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { IconButton } from '@cbrebuild/blocks';
import { Link, useHistory } from 'react-router-dom';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import RequiredLabeledItem from './RequiredLabeledItem';
import { fetchDealContacts, addDealContact } from '../../../redux/actions/deal-contacts-actions';
import { validateAddress, isValidPostal } from '../voucher-validation';
import companiesService from '../../../services/companies-service';
import { getRoleKey, isPartyClient, isPartyPocRequired } from '../../../utils/transaction-utils';
import states from '../../../data/states.json';

const getContactName = contact => [contact?.given_name, contact?.surname].filter(Boolean).join(' ');
const withLeaseDetailsSection = (url, section) => `${url}?section=${section}`;

// State dropdown options for the "Before you submit" panel's per-company Address rows --
// `states.json` already stores US entries as `{value: 'CA', display_name: 'California', ...}`,
// matching the `{value, display_name}` shape `RequiredLabeledItem`'s `options` prop expects.
const usStateOptions = states
  .filter(s => s.country_id === 'US')
  .map(({ value, display_name }) => ({ value, display_name }));

const CompanyContactReview = ({ partyType }) => {
  const {
    commercialProperties, deal, dealContacts, primaryContact, voucher, updatedCompanyInfo,
  } = useSelector(state => ({
    commercialProperties: state.voucher.commercialProperties,
    deal: state.deal.deal,
    dealContacts: state.dealContacts.contacts,
    primaryContact: state.dealContacts.primaryContact,
    voucher: state.voucher.voucher,
    updatedCompanyInfo: state.voucher.updatedInfo,
  }));
  const [company, setCompany] = useState();
  const [companyIsLoaded, setCompanyIsLoaded] = useState(false);
  // Mirrors `companyIsLoaded` below: this component fetches its own contact data via
  // `fetchDealContacts` AFTER it mounts (unlike `deal`/`voucher`, which are already loaded
  // by the time ReviewTab itself renders), so `contact` is transiently undefined for the
  // first render or two. Passed as `isReview` to the Point of Contact fields below so
  // `RequiredLabeledItem` doesn't mistake that momentary "not loaded yet" blip for the
  // field being genuinely missing (2026-07-31 bug fix).
  const [contactsIsLoaded, setContactsIsLoaded] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  useEffect(() => {
    // Wrapped in `Promise.resolve` (rather than chaining `.then` directly off `dispatch(...)`)
    // since some tests configure their mock store's `dispatch` to return the plain action
    // object instead of a real thunk promise -- `Promise.resolve` normalizes either case.
    Promise.resolve(dispatch(fetchDealContacts({
      deal: deal.id,
      ordering: '-is_primary',
      page: 1,
      page_size: 100,
    }))).then(() => setContactsIsLoaded(true));
  }, [deal.id, dispatch, partyType]);

  const dealType = deal.deal_type;
  const isMiscDeal = dealType === 'consulting' || dealType === 'other';
  const isRepped = isPartyClient(deal, partyType);
  const isPartyType = args => !!_.find(args, arg => arg === partyType);
  const isLeaseLandlordDealWithRequiredLandlordPoc =
    dealType === 'lease' && partyType === 'landlord' && getRoleKey(deal.rep_role, 'landlord') === 'client';
  const isPocRequired =
    isPartyPocRequired(deal, partyType) || isLeaseLandlordDealWithRequiredLandlordPoc;
  const companyDealFieldPath = isMiscDeal ? 'client' : getRoleKey(deal.rep_role, partyType);
  // Must get company information using companies api, not from the deal
  const companyReferenceFromDeal = deal[companyDealFieldPath];
  const companyId = typeof companyReferenceFromDeal === 'object'
    ? companyReferenceFromDeal?.id
    : companyReferenceFromDeal;
  // Latest ref (not a dep) so the fetch effect below only re-checks this when `companyId`
  // itself changes, instead of re-running (and possibly skipping) whenever ANY company
  // elsewhere on the page updates state.voucher.updatedInfo.
  const updatedCompanyInfoRef = useRef(updatedCompanyInfo);
  updatedCompanyInfoRef.current = updatedCompanyInfo;
  useEffect(() => {
    if (!companyId) {
      setCompany(undefined);
      // No company id means there is nothing to fetch: treat as loaded/missing so
      // required company fields can still validate/register in the Review panel.
      setCompanyIsLoaded(true);
      return;
    }
    // Company Name search (ReviewCompanyNameSearchControl) already dispatched the full
    // record into state.voucher.updatedInfo right after creating/selecting it (picked up
    // by the effect below) -- skip the redundant GET for data we already have in hand,
    // matching the live voucher flow's useCompanyState hook (its own skipNextHydrationRef).
    if (updatedCompanyInfoRef.current?.id === companyId) return;
    setCompanyIsLoaded(false);
    companiesService.getCompany(companyId)
      .then(res => setCompany(res))
      .catch(() => {
        // Failed fetch should not permanently suppress validation/registration.
        setCompany(undefined);
      })
      .then(() => setCompanyIsLoaded(true));
  }, [companyId]);

  useEffect(() => {
    if (!updatedCompanyInfo?.id) return;
    if (updatedCompanyInfo.id !== companyId) return;
    setCompany(updatedCompanyInfo);
    setCompanyIsLoaded(true);
  }, [updatedCompanyInfo, companyId]);

  const allDealContacts = [primaryContact, ...dealContacts].filter(Boolean);
  const updatedDealContact = allDealContacts.length
    ? (allDealContacts.filter(con => con.contact_type === partyType))[0]
    : undefined;
  const contact = updatedDealContact && {
    ...updatedDealContact,
    fullName: getContactName(updatedDealContact),
  };

  // Mirrors VoucherContactForm.js's own mount-time `addDealContact` call: without a
  // contact record to patch, `contactSearch`'s `editTarget.targetId` (contact?.id) is
  // undefined, so ReviewTab.js's `updateDealContact` case silently no-ops on selection --
  // First/Last Name search results never got saved and no other Point of Contact fields
  // filled in (2026-08-11 bug report).
  const pendingContactCreateRef = useRef(false);
  useEffect(() => {
    if (!contactsIsLoaded || contact || pendingContactCreateRef.current) return;
    pendingContactCreateRef.current = true;
    Promise.resolve(dispatch(addDealContact({
      contact_type: partyType,
      deal: deal.id,
      email_addresses: [],
      given_name: '',
      is_primary: false,
      phone_numbers: [],
      surname: '',
      create_personal_contact: false,
      display_toast: false,
    }))).then(() => { pendingContactCreateRef.current = false; });
  }, [contactsIsLoaded, contact, partyType, deal.id, dispatch]);

  let tenantPriorLocation;
  if (isPartyType(['tenant'])) {
    const tenantPriorLocations = commercialProperties.filter(prop => prop.location_type_new === false);
    if (tenantPriorLocations.length) {
      tenantPriorLocation = { ...tenantPriorLocations[0] };
    } else {
      tenantPriorLocation = null;
    }
  }

  const strings = {
    title: '',
    subtitle: '',
    url: '',
  };
  switch (partyType) {
    case 'tenant':
      strings.title = 'Tenant Information';
      strings.subtitle = 'Tenant';
      strings.url = VOUCHER_ROUTES[1].routes[0].path;
      break;
    case 'landlord':
      strings.title = 'Landlord Information';
      strings.subtitle = 'Landlord';
      strings.url = VOUCHER_ROUTES[1].routes[0].path;
      break;
    case 'seller':
      strings.title = 'Seller Information';
      strings.subtitle = 'Seller';
      strings.url = VOUCHER_ROUTES[1].routes[6].path;
      break;
    case 'buyer':
      strings.title = 'Buyer Information';
      strings.subtitle = 'Buyer';
      strings.url = VOUCHER_ROUTES[1].routes[5].path;
      break;
    case 'consulting':
    case 'other':
      strings.title = 'Client Information';
      strings.url = VOUCHER_ROUTES[1].routes[8].path;
      break;
    default:
      break;
  }
  strings.url = strings.url.replace(':id', voucher.deal);
  if (dealType === 'lease' && partyType === 'tenant') {
    strings.url = withLeaseDetailsSection(strings.url, 'tenantInformation');
  }
  if (dealType === 'lease' && partyType === 'landlord') {
    strings.url = withLeaseDetailsSection(strings.url, 'landlordInformation');
  }

  // Non-digit ZIP (e.g. "adsda") -- mirrors CommissionsReview.js's `pursuitIdFormatInvalid`:
  // shown immediately (not gated behind page-level required-field validation), keeps the raw
  // value visible instead of blanking it, and surfaces the same wording used in the live
  // voucher flow's AddressForm.js.
  const zipFormatInvalid = !!company?.postal && !isValidPostal(company.postal, company?.country);
  const ZIP_FORMAT_MESSAGE = 'Zip must be 5 digits or ZIP+4';

  return (
    <div className="voucher-review-group company-contact-review">
      <div className="navigation">
        <Link className="h2 link" to={strings.url}>{strings.title}</Link>
        <IconButton iconName="edit" onClick={() => { history.push(strings.url); }} />
      </div>
      <div className="content">
        <div className="two-cols">
          <div className="col-left">
            <h3>{strings.subtitle}</h3>
            <RequiredLabeledItem
              label="Company Name"
              item={company?.name}
              isReview={companyIsLoaded}
              fieldId={`company-contact-${partyType}-name`}
              section={strings.title}
              controlType="companySearch"
              editTarget={{
                reduxAction: 'updateDeal',
                fieldPath: companyDealFieldPath,
                currentCompany: company,
              }}
            />
            {isPartyType(['tenant', 'buyer']) &&
              <RequiredLabeledItem
                label="Business Type / Use ID"
                item={company?.voucher_naics_category}
                notRequired={voucher.is_early_invoice}
                isReview={companyIsLoaded}
                fieldId={`company-contact-${partyType}-business-type`}
                section={strings.title}
                controlType="naicsSearch"
                editTarget={{
                  reduxAction: 'updateCompany',
                  fieldPath: 'voucher_naics_choice',
                  targetId: company?.id,
                  dealFieldPath: companyDealFieldPath,
                }}
              />
            }
            <RequiredLabeledItem
              label="Address"
              item={company?.address}
              notRequired={voucher.is_early_invoice}
              isReview={companyIsLoaded}
              fieldId={`company-contact-${partyType}-address`}
              section={strings.title}
              controlType="text"
              editTarget={{
                reduxAction: 'updateCompany', fieldPath: 'address', targetId: company?.id, dealFieldPath: companyDealFieldPath,
              }}
            />
            {/* Suite/Floor line -- always optional, so kept as a plain read-only summary
                (matching GWS Client/Parent Company below) instead of a "Before you submit"
                editable field. */}
            <RequiredLabeledItem label="Address Line 2" item={company?.address2} notRequired isReview={companyIsLoaded} />
            <RequiredLabeledItem
              label="City"
              item={company?.city}
              notRequired={voucher.is_early_invoice}
              isReview={companyIsLoaded}
              fieldId={`company-contact-${partyType}-city`}
              section={strings.title}
              controlType="text"
              editTarget={{
                reduxAction: 'updateCompany', fieldPath: 'city', targetId: company?.id, dealFieldPath: companyDealFieldPath,
              }}
            />
            <RequiredLabeledItem
              label="State"
              item={company?.territory}
              notRequired={voucher.is_early_invoice}
              isReview={companyIsLoaded}
              fieldId={`company-contact-${partyType}-state`}
              section={strings.title}
              controlType="select"
              options={usStateOptions}
              editTarget={{
                reduxAction: 'updateCompany', fieldPath: 'territory', targetId: company?.id, dealFieldPath: companyDealFieldPath,
              }}
            />
            <RequiredLabeledItem
              label="Zip"
              item={company?.postal}
              notRequired={voucher.is_early_invoice}
              isReview={companyIsLoaded}
              fieldId={`company-contact-${partyType}-zip`}
              section={strings.title}
              controlType="zip"
              // Matches the "Pursuit ID must start with SF..." pattern in CommissionsReview.js:
              // `invalid` keeps the raw (non-digit) value visible/registered instead of blanking
              // it, while `requiredMessage` swaps in the format-specific wording.
              invalid={zipFormatInvalid}
              requiredMessage={zipFormatInvalid ? ZIP_FORMAT_MESSAGE : null}
              editTarget={{
                reduxAction: 'updateCompany', fieldPath: 'postal', targetId: company?.id, dealFieldPath: companyDealFieldPath,
              }}
            />
            {zipFormatInvalid && (
              <p className="voucher-error-text">{ZIP_FORMAT_MESSAGE}</p>
            )}
            {isPartyType(['buyer']) && (
              <>
                <RequiredLabeledItem label="Purchaser Type" item={company?.voucher_purchaser_type} notRequired={voucher.is_early_invoice} isReview />
                <RequiredLabeledItem
                  label="Investor Type"
                  item={company?.voucher_investor_type}
                  notRequired={company && (company.voucher_purchaser_type !== '01-Investor')}
                  isReview
                />
              </>
            )}
            <RequiredLabeledItem label="GWS Client" item={company?.gws_client} notRequired isReview />
            <RequiredLabeledItem label="Parent Company" item={company?.parent_company} notRequired isReview />
            {isPartyType(['tenant']) &&
              <RequiredLabeledItem label="Tenant Credit Rating" item={company?.credit_rating} notRequired isReview />
            }
            {!isMiscDeal &&
              <RequiredLabeledItem label="Is Repped?" item={isRepped} format="bool" notRequired isReview />
            }
            {isPartyType(['tenant']) &&
              <RequiredLabeledItem label="Tenant Relocating" item={!!tenantPriorLocation} format="bool" notRequired isReview />
            }
          </div>
          <div className="col-right">
            <h3>Point of Contact</h3>
            <RequiredLabeledItem
              label="First Name"
              item={(contact && contact.given_name) || null}
              notRequired={!isPocRequired}
              isReview={contactsIsLoaded}
              fieldId={isPocRequired ? `company-contact-${partyType}-poc-first-name` : undefined}
              section={strings.title}
              subSection="Point of Contact"
              controlType="contactSearch"
              editTarget={{
                reduxAction: 'updateDealContact',
                fieldPath: 'given_name',
                targetId: contact?.id,
                phoneNumbers: contact?.phone_numbers,
                emailAddresses: contact?.email_addresses,
              }}
            />
            <RequiredLabeledItem
              label="Last Name"
              item={(contact && contact.surname) || null}
              notRequired={!isPocRequired}
              isReview={contactsIsLoaded}
              fieldId={isPocRequired ? `company-contact-${partyType}-poc-last-name` : undefined}
              section={strings.title}
              subSection="Point of Contact"
              controlType="contactSearch"
              editTarget={{
                reduxAction: 'updateDealContact',
                fieldPath: 'surname',
                targetId: contact?.id,
                phoneNumbers: contact?.phone_numbers,
                emailAddresses: contact?.email_addresses,
              }}
            />
            <RequiredLabeledItem
              label="Phone #"
              item={(contact?.phone_numbers?.[0]?.number || null)}
              notRequired={!isPocRequired}
              isReview={contactsIsLoaded}
              fieldId={isPocRequired ? `company-contact-${partyType}-poc-phone` : undefined}
              section={strings.title}
              subSection="Point of Contact"
              controlType="phone"
              editTarget={{
                reduxAction: 'updateDealContact', fieldPath: 'phone', targetId: contact?.id, phoneNumbers: contact?.phone_numbers,
              }}
            />
            <RequiredLabeledItem
              label="Email Address"
              item={(contact?.email_addresses?.[0]?.address || null)}
              notRequired={!isPocRequired}
              isReview={contactsIsLoaded}
              fieldId={isPocRequired ? `company-contact-${partyType}-poc-email` : undefined}
              section={strings.title}
              subSection="Point of Contact"
              controlType="text"
              editTarget={{
                reduxAction: 'updateDealContact', fieldPath: 'email', targetId: contact?.id, emailAddresses: contact?.email_addresses,
              }}
            />
            <RequiredLabeledItem label="Job Title" item={(contact && contact.job_title) || null} notRequired isReview />
          </div>
        </div>
      </div>
      {isPartyType(['tenant']) && tenantPriorLocation &&
        <div className="content">
          <h3>Tenant Prior Address</h3>
          <RequiredLabeledItem
            label="Prior Address"
            item={validateAddress(tenantPriorLocation, true) && tenantPriorLocation}
            format="address"
            notRequired={voucher.is_early_invoice}
            isReview
          />
          <RequiredLabeledItem
            label="Prior Rent Area (SqFt)"
            item={tenantPriorLocation.spaces[0]?.space_total_square_feet}
            format="number"
            notRequired
            isReview
          />
          <RequiredLabeledItem label="Percentage Lease" item={tenantPriorLocation.percentage_rent} format="bool" notRequired isReview />
        </div>
      }
    </div>
  );
};

CompanyContactReview.propTypes = {
  partyType: PropTypes.oneOf(['tenant', 'landlord', 'seller', 'buyer', 'consulting', 'other']).isRequired,
};

export default CompanyContactReview;
